/**
 * Push notification registration. Native only: registers with APNs / FCM
 * and hands the token to the API. The web build does nothing here; reminders
 * still arrive in-app over socket.io.
 */
import { registerPlugin } from '@capacitor/core'
import { api } from '@/api/http'
import { hasPlugin, isNative, platform } from './platform'

let registered = false

const pushSupport = registerPlugin<{ available(): Promise<{ available: boolean }> }>('PushSupport')

/**
 * Whether this build can receive push. Android needs Firebase set up at build
 * time (google-services.json); without it the push plugin's register() crashes
 * the app, which no try/catch here can stop. iOS uses APNs directly.
 */
async function pushConfigured(): Promise<boolean> {
  if (platform() !== 'android') return true
  if (!hasPlugin('PushSupport')) return false
  try {
    return (await pushSupport.available()).available
  } catch {
    return false
  }
}

export async function registerForPush(onOpen?: (link: string | null) => void): Promise<void> {
  if (registered || !isNative() || !hasPlugin('PushNotifications')) return
  registered = true
  // Don't ask for permission for something that can't work.
  if (!(await pushConfigured())) return
  const { PushNotifications } = await import('@capacitor/push-notifications')
  const perm = await PushNotifications.checkPermissions()
  const granted =
    perm.receive === 'granted' ||
    (perm.receive === 'prompt' &&
      (await PushNotifications.requestPermissions()).receive === 'granted')
  if (!granted) return
  // reminder-agent sends to this channel, so riders can tune reminders in
  // Android settings. Older app builds without it get FCM's fallback channel.
  if (platform() === 'android') {
    await PushNotifications.createChannel({
      id: 'reminders',
      name: 'Reminders',
      description: 'When a part or service falls due',
      importance: 3,
    }).catch(() => {})
  }
  await PushNotifications.addListener('registration', ({ value }) => {
    void api('/me/devices', { method: 'POST', body: { platform: platform(), token: value } }).catch(
      () => {},
    )
  })
  await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const link = (notification.data as { link?: string } | undefined)?.link ?? null
    onOpen?.(link)
  })
  await PushNotifications.register()
}
